import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { PropertyLocationMap } from '@/components/properties/PropertyLocationMap';
import {
  LocationHintUnavailableError,
  toNumericCoordinate,
  usePropertyLocationHint,
  useSetPropertyLocation,
} from '@/routes/properties/useProperty';

/**
 * Placing a pin by hand on a property the geocoder could not match (M1-36).
 *
 * <b>The other half of M1-26.</b> That story shipped the CORRECTION — dragging
 * a rooftop match onto the long rural driveway it missed — but only for a
 * property that already had a pin. `PUT /properties/{id}/location` accepted a
 * `NoMatch` row all along and simply had no caller. Until this, the office's
 * only route to a pin on an unmatchable address was editing the stored address
 * until the provider happened to match something: mutating real data to reach a
 * feature.
 *
 * ⚠️ <b>The map is not opened until asked.</b> Centring it costs a geocode call
 * at the provider, so the button is the thing that spends it — a property page
 * that opened a map nobody wanted would buy that answer on every visit.
 */
export function PlacePropertyPin({
  propertyId,
  onPlaced,
}: {
  propertyId: string;
  /** Announced by the page, which owns the live region — raw facts, not a built string. */
  onPlaced: () => void;
}) {
  const { t } = useTranslation();
  const [placing, setPlacing] = useState(false);
  const hint = usePropertyLocationHint(propertyId, placing);
  const setLocation = useSetPropertyLocation(propertyId);

  if (!placing) {
    return (
      <div className="mt-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setPlacing(true)}>
          {t('placePropertyPin.action')}
        </Button>
      </div>
    );
  }

  if (hint.isPending) {
    return (
      <p className="mt-2 text-sm text-[var(--color-muted-foreground)]" role="status">
        {t('placePropertyPin.finding')}
      </p>
    );
  }

  if (hint.isError) {
    // ⚠️ Two different failures, said differently. "We could not centre the
    // map" is not "something went wrong" — the first is about this address and
    // leaves panning as a route, the second is transport.
    const unavailable = hint.error instanceof LocationHintUnavailableError;
    return (
      <div className="mt-2 space-y-2">
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {unavailable ? t('placePropertyPin.noStartingPoint') : t('placePropertyPin.failed')}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => setPlacing(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    );
  }

  // ⚠️ The generated client types a `double` as `number | string`, so these
  // arrive as strings often enough to matter — the fourth field in this
  // codebase to need the coercion (latitude, longitude, sortOrder, the licence
  // counts). Leaflet takes a string centre without complaint and then puts the
  // map somewhere off the coast of Africa.
  const latitude = toNumericCoordinate(hint.data.latitude);
  const longitude = toNumericCoordinate(hint.data.longitude);
  if (latitude == null || longitude == null) {
    // ⚠️ Cancel, not just the alert. This branch used to render a bare
    // paragraph, which left `placing` true with the paragraph sitting where the
    // button had been: no control on screen could get back, so the office's only
    // way out was reloading the page. An error state has to offer a way out of
    // itself even when — especially when — the way in is one nobody can explain.
    return (
      <div className="mt-2 space-y-2">
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('placePropertyPin.failed')}
        </p>
        <Button type="button" variant="outline" size="sm" onClick={() => setPlacing(false)}>
          {t('common.cancel')}
        </Button>
      </div>
    );
  }

  // ⚠️ ONE normalisation, for the zoom and the sentence alike. These used to
  // disagree: the zoom fell back to Region for a precision it did not know while
  // the sentence interpolated the raw value into an i18n key. A tier the API
  // grew later would have put the literal string
  // "placePropertyPin.centredOn.Country" on screen above a correctly zoomed map.
  // Region is the conservative fallback in both: it claims the least.
  const precision = PRECISIONS.includes(hint.data.precision) ? hint.data.precision : 'Region';

  return (
    <div className="mt-2 space-y-2">
      {/*
        ⚠️ Says where it started and WHY, in words. The pin is a town centroid,
        not the property, and a map that opens somewhere unexplained reads as a
        wrong answer rather than a starting point. `web/CONVENTIONS.md`'s
        never-colour-only rule, applied to confidence rather than to state.
      */}
      <p className="text-sm text-[var(--color-muted-foreground)]" role="status">
        {t(`placePropertyPin.centredOn.${precision}`, { place: hint.data.label })}
      </p>
      <PropertyLocationMap
        latitude={latitude}
        longitude={longitude}
        editable
        // Zoom follows how precisely we know where to look. A state centroid at
        // street zoom would look like a confident answer about one field.
        zoom={ZOOM_BY_PRECISION[precision]}
        onCorrect={async (latitude, longitude) => {
          await setLocation.mutateAsync({ latitude, longitude });
          setPlacing(false);
          onPlaced();
        }}
      />
      <Button type="button" variant="outline" size="sm" onClick={() => setPlacing(false)}>
        {t('common.cancel')}
      </Button>
    </div>
  );
}

/**
 * How far in to open, per tier. Deliberately not street level for any of them:
 * every tier here is a place the property is NEAR, never where it IS.
 */
const ZOOM_BY_PRECISION: Record<string, number> = {
  Locality: 13,
  PostalCode: 13,
  Region: 7,
};

/** The tiers the API can name. Anything else is read as `Region`. */
const PRECISIONS: readonly string[] = Object.keys(ZOOM_BY_PRECISION);
